/**
 * 스크립트 capability 정책 — 결정 #4/#5/#17의 보안 핵심(플랫폼 중립).
 *
 * "무엇이 허용되는가"를 순수 함수로 결정한다. 실제 실행(QuickJS-WASM)은 web의
 * ScriptHost가 맡지만, 권한 판정·도메인 allowlist·시드 RNG 같은 보안 규칙은
 * 여기서 정의해 단위 테스트로 고정한다(WASM 없이 검증 가능).
 *
 * 등급(결정 #17):
 *  - safe: variables(get/set), mutateMessages. 동의 불필요.
 *  - elevated: network(중개 fetch), storage. 명시적 consent + allowlist 필요.
 */
import type { ScriptCapabilities } from "../domain/index.js";

/** capability 종류. */
export type Capability = "variables" | "mutateMessages" | "storage" | "network";

/** safe 등급(동의 불필요, 결정 #17). */
const SAFE: Capability[] = ["variables", "mutateMessages"];

/** 한 capability가 elevated(동의 필요)인지. */
export function isElevated(cap: Capability): boolean {
  return !SAFE.includes(cap);
}

/**
 * 스크립트가 특정 capability를 "지금" 쓸 수 있는지 판정한다(결정 #17).
 *  - 요청하지 않은 capability는 항상 불가.
 *  - safe 등급은 요청만 했으면 가능.
 *  - elevated 등급은 요청 + consentGranted=true 여야 가능.
 */
export function isCapabilityAllowed(
  cap: Capability,
  caps: ScriptCapabilities | undefined,
  consentGranted: boolean,
): boolean {
  if (!caps) return false;
  const requested =
    cap === "network" ? !!caps.network : cap === "storage" ? !!caps.storage : !!caps[cap];
  if (!requested) return false;
  if (isElevated(cap)) return consentGranted;
  return true;
}

/**
 * 중개 fetch 도메인 allowlist 매칭(결정 #17의 SSRF/유출 방어 핵심).
 *
 * 규칙(보수적):
 *  - https만 허용(평문/기타 스킴 차단).
 *  - 자격증명 포함 URL(user:pass@) 차단.
 *  - 호스트가 allowlist 항목과 정확히 일치하거나 그 서브도메인일 때만 허용.
 *    (예: allow "example.com" → "api.example.com" OK, "evil-example.com" NO)
 *  - 사설/링크로컬/메타데이터 호스트는 항상 차단(SSRF).
 */
const BLOCKED_HOST_PATTERNS = [
  /^localhost$/i,
  /^127\./,
  /^10\./,
  /^192\.168\./,
  /^169\.254\./,
  /^172\.(1[6-9]|2\d|3[01])\./,
  /^\[?::1\]?$/,
  /\.internal$/i,
  /\.local$/i,
];

export function isUrlAllowed(urlStr: string, allowlist: string[]): boolean {
  if (!allowlist || allowlist.length === 0) return false;
  let url: URL;
  try {
    url = new URL(urlStr);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;
  if (url.username || url.password) return false;
  const host = url.hostname.toLowerCase();
  if (BLOCKED_HOST_PATTERNS.some((re) => re.test(host))) return false;

  return allowlist.some((entry) => {
    const allowed = entry.trim().toLowerCase().replace(/^\*\./, "");
    if (!allowed) return false;
    return host === allowed || host.endsWith(`.${allowed}`);
  });
}

/**
 * 결정적 RNG(결정 #4: 재현 가능한 스크립트 실행). 호스트가 시드를 주입하면
 * 같은 시드 → 같은 난수열이라 리플레이/테스트가 안정적이다. mulberry32.
 */
export function makeSeededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return function random(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 실행 리소스 한도(결정 #4/#17: DoS 방어). web 호스트가 enforce한다. */
export interface ScriptLimits {
  /** 단일 invoke 최대 실행 시간(ms). */
  timeoutMs: number;
  /** 최대 메모리(bytes). QuickJS 메모리 캡. */
  memoryBytes: number;
  /** runtrigger 등 재귀 호출 상한(consentGranted여도 유지 권장). */
  maxRecursion: number;
}

/** 6s+ 기기를 보호하는 기본 한도(결정 #4 risu-migration §6). */
export const DEFAULT_SCRIPT_LIMITS: ScriptLimits = {
  timeoutMs: 1000,
  memoryBytes: 16 * 1024 * 1024, // 16MB
  maxRecursion: 10,
};
